import { useState, type ReactNode } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { Activity, Calculator, FileText, Landmark } from "lucide-react";
import toast from "react-hot-toast";
import PageHeader from "@/components/common/PageHeader";
import { usageRevenueService } from "@/services/usage-revenue.service";

const tabs = [
  "Meters",
  "Usage events",
  "Usage charges",
  "Revenue recognition",
] as const;

export default function UsageRevenuePage() {
  const [tab, setTab] = useState<(typeof tabs)[number]>("Meters");
  const client = useQueryClient();
  const [meters, events, aggregates, schedules, forecast] = useQueries({
    queries: [
      { queryKey: ["usage-meters"], queryFn: usageRevenueService.meters },
      {
        queryKey: ["usage-events"],
        queryFn: () => usageRevenueService.events(),
      },
      {
        queryKey: ["usage-aggregates"],
        queryFn: () => usageRevenueService.aggregates(),
      },
      {
        queryKey: ["usage-revenue-schedules"],
        queryFn: () => usageRevenueService.revenueSchedules(),
      },
      {
        queryKey: ["usage-revenue-forecast"],
        queryFn: usageRevenueService.revenueForecast,
      },
    ],
  });
  const refresh = () =>
    client.invalidateQueries({
      predicate: (query) => String(query.queryKey[0]).startsWith("usage-"),
    });
  const meterRows = meters.data ?? [];
  const eventRows = events.data ?? [];
  const aggregateRows = aggregates.data ?? [];
  const scheduleRows = schedules.data ?? [];
  return (
    <div className="space-y-6">
      <PageHeader
        title="Usage & revenue automation"
        subtitle="Metered pricing, immutable usage events, invoice-ready charges, and finance-grade revenue schedules."
      />
      <div className="grid gap-4 md:grid-cols-4">
        <Metric
          label="Active meters"
          value={meterRows.filter((item: any) => item.isActive).length}
          icon={Activity}
        />
        <Metric
          label="Usage events"
          value={eventRows.length}
          icon={Calculator}
        />
        <Metric
          label="Open charges"
          value={
            aggregateRows.filter((item: any) => item.status === "OPEN").length
          }
          icon={FileText}
        />
        <Metric
          label="Revenue schedules"
          value={
            scheduleRows.filter((item: any) => item.status === "ACTIVE").length
          }
          icon={Landmark}
        />
      </div>
      <TabBar values={tabs} value={tab} onChange={setTab} />
      {tab === "Meters" && (
        <MetersPanel items={meterRows} onChanged={refresh} />
      )}
      {tab === "Usage events" && (
        <EventsPanel items={eventRows} meters={meterRows} onChanged={refresh} />
      )}
      {tab === "Usage charges" && (
        <AggregatesPanel
          items={aggregateRows}
          meters={meterRows}
          onChanged={refresh}
        />
      )}
      {tab === "Revenue recognition" && (
        <RevenuePanel
          items={scheduleRows}
          forecast={forecast.data ?? []}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

function MetersPanel({
  items,
  onChanged,
}: {
  items: any[];
  onChanged: () => void;
}) {
  const [form, setForm] = useState({
    code: "",
    name: "",
    unit: "event",
    aggregation: "SUM",
    pricingModel: "PER_UNIT",
    unitPrice: "0",
    includedQuantity: "0",
    resetPeriod: "MONTHLY",
    tiers: '[{"upTo":1000,"unitPrice":0.5},{"upTo":null,"unitPrice":0.35}]',
  });
  const create = useMutation({
    mutationFn: () =>
      usageRevenueService.createMeter({
        code: form.code,
        name: form.name,
        unit: form.unit,
        aggregation: form.aggregation,
        pricingModel: form.pricingModel,
        unitPrice: Number(form.unitPrice),
        includedQuantity: Number(form.includedQuantity),
        resetPeriod: form.resetPeriod,
        tiers: ["VOLUME", "GRADUATED"].includes(form.pricingModel)
          ? JSON.parse(form.tiers)
          : undefined,
      }),
    onSuccess: () => {
      onChanged();
      toast.success("Usage meter created");
    },
    onError: () => toast.error("Check meter values and pricing tiers"),
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      usageRevenueService.setMeterStatus(id, active),
    onSuccess: onChanged,
  });
  return (
    <div className="space-y-4">
      <Panel>
        <h3 className="font-bold">Define a usage meter</h3>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Input
            label="Code"
            value={form.code}
            onChange={(value) => setForm({ ...form, code: value })}
          />
          <Input
            label="Name"
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
          />
          <Input
            label="Unit"
            value={form.unit}
            onChange={(value) => setForm({ ...form, unit: value })}
          />
          <Select
            label="Aggregation"
            value={form.aggregation}
            values={["SUM", "MAX", "LAST"]}
            onChange={(value) => setForm({ ...form, aggregation: value })}
          />
          <Select
            label="Pricing model"
            value={form.pricingModel}
            values={["PER_UNIT", "FLAT", "VOLUME", "GRADUATED"]}
            onChange={(value) => setForm({ ...form, pricingModel: value })}
          />
          <Input
            label="Unit / flat price"
            value={form.unitPrice}
            onChange={(value) => setForm({ ...form, unitPrice: value })}
          />
          <Input
            label="Included quantity"
            value={form.includedQuantity}
            onChange={(value) => setForm({ ...form, includedQuantity: value })}
          />
          <Select
            label="Reset period"
            value={form.resetPeriod}
            values={["DAILY", "MONTHLY", "ANNUAL"]}
            onChange={(value) => setForm({ ...form, resetPeriod: value })}
          />
        </div>
        {["VOLUME", "GRADUATED"].includes(form.pricingModel) && (
          <div className="mt-3">
            <Input
              label="Pricing tiers JSON (final upTo must be null)"
              value={form.tiers}
              onChange={(value) => setForm({ ...form, tiers: value })}
            />
          </div>
        )}
        <div className="mt-4">
          <Button
            disabled={!form.code || !form.name}
            onClick={() => create.mutate()}
          >
            Create meter
          </Button>
        </div>
      </Panel>
      <div className="grid gap-3 xl:grid-cols-2">
        {items.length ? (
          items.map((item) => (
            <Panel key={item.id}>
              <div className="flex justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold">
                      {item.code} · {item.name}
                    </h3>
                    <Badge value={item.isActive ? "ACTIVE" : "DISABLED"} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.aggregation} {item.unit} · {item.pricingModel} · ₹
                    {item.unitPrice} · {item.includedQuantity} included /{" "}
                    {item.resetPeriod.toLowerCase()}
                  </p>
                  <p className="mt-2 text-xs text-slate-400">
                    {item._count?.events ?? 0} events ·{" "}
                    {item._count?.aggregates ?? 0} billing periods
                  </p>
                </div>
                <Button
                  secondary
                  onClick={() =>
                    toggle.mutate({ id: item.id, active: !item.isActive })
                  }
                >
                  {item.isActive ? "Disable" : "Enable"}
                </Button>
              </div>
            </Panel>
          ))
        ) : (
          <Empty text="No usage meters." />
        )}
      </div>
    </div>
  );
}

function EventsPanel({
  items,
  meters,
  onChanged,
}: {
  items: any[];
  meters: any[];
  onChanged: () => void;
}) {
  const [form, setForm] = useState({
    meterCode: "",
    tenantId: "",
    subscriptionId: "",
    externalId: crypto.randomUUID() as string,
    quantity: "1",
    occurredAt: new Date().toISOString().slice(0, 16),
    dimensions: "{}",
  });
  const ingest = useMutation({
    mutationFn: () =>
      usageRevenueService.ingestEvent({
        ...form,
        subscriptionId: form.subscriptionId || undefined,
        quantity: Number(form.quantity),
        occurredAt: new Date(form.occurredAt).toISOString(),
        dimensions: JSON.parse(form.dimensions),
        source: "ADMIN",
      }),
    onSuccess: () => {
      onChanged();
      setForm({ ...form, externalId: crypto.randomUUID() });
      toast.success("Usage event accepted");
    },
    onError: () =>
      toast.error("Usage event failed validation or idempotency checks"),
  });
  return (
    <div className="space-y-4">
      <Panel>
        <h3 className="font-bold">Ingest a usage event</h3>
        <p className="mt-1 text-xs text-slate-500">
          External ID is immutable and deduplicates retries for a tenant and
          meter.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Select
            label="Meter"
            value={form.meterCode}
            values={[
              "",
              ...meters
                .filter((item) => item.isActive)
                .map((item) => item.code),
            ]}
            labels={[
              "Select meter",
              ...meters
                .filter((item) => item.isActive)
                .map((item) => `${item.code} · ${item.name}`),
            ]}
            onChange={(value) => setForm({ ...form, meterCode: value })}
          />
          <Input
            label="Tenant UUID"
            value={form.tenantId}
            onChange={(value) => setForm({ ...form, tenantId: value })}
          />
          <Input
            label="Subscription UUID (optional)"
            value={form.subscriptionId}
            onChange={(value) => setForm({ ...form, subscriptionId: value })}
          />
          <Input
            label="External event ID"
            value={form.externalId}
            onChange={(value) => setForm({ ...form, externalId: value })}
          />
          <Input
            label="Quantity"
            value={form.quantity}
            onChange={(value) => setForm({ ...form, quantity: value })}
          />
          <Input
            type="datetime-local"
            label="Occurred at"
            value={form.occurredAt}
            onChange={(value) => setForm({ ...form, occurredAt: value })}
          />
          <Input
            label="Dimensions JSON"
            value={form.dimensions}
            onChange={(value) => setForm({ ...form, dimensions: value })}
          />
        </div>
        <div className="mt-4">
          <Button
            disabled={!form.meterCode || !form.tenantId || !form.externalId}
            onClick={() => ingest.mutate()}
          >
            Ingest event
          </Button>
        </div>
      </Panel>
      <div className="space-y-3">
        {items.length ? (
          items.map((item) => (
            <Panel key={item.id}>
              <div className="flex justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold">
                      {item.meter?.code} · {item.quantity} {item.meter?.unit}
                    </h3>
                    <Badge value={item.source} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.tenant?.name} ·{" "}
                    {new Date(item.occurredAt).toLocaleString()}
                  </p>
                  <p className="mt-1 font-mono text-[10px] text-slate-400">
                    {item.externalId}
                  </p>
                </div>
              </div>
            </Panel>
          ))
        ) : (
          <Empty text="No usage events." />
        )}
      </div>
    </div>
  );
}

function AggregatesPanel({
  items,
  meters,
  onChanged,
}: {
  items: any[];
  meters: any[];
  onChanged: () => void;
}) {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1)
    .toISOString()
    .slice(0, 10);
  const [form, setForm] = useState({
    tenantId: "",
    meterCode: "",
    periodStart: first,
    periodEnd: next,
  });
  const calculate = useMutation({
    mutationFn: () =>
      usageRevenueService.calculateAggregate({
        ...form,
        periodStart: new Date(
          `${form.periodStart}T00:00:00.000Z`,
        ).toISOString(),
        periodEnd: new Date(`${form.periodEnd}T00:00:00.000Z`).toISOString(),
      }),
    onSuccess: () => {
      onChanged();
      toast.success("Usage charge calculated");
    },
  });
  const finalize = useMutation({
    mutationFn: ({ id, invoiceId }: { id: string; invoiceId: string }) =>
      usageRevenueService.finalizeAggregate(id, {
        invoiceId,
        taxRate: 18,
        reason: "Administrator finalized reviewed usage onto the invoice",
      }),
    onSuccess: () => {
      onChanged();
      toast.success("Usage charge added to invoice");
    },
  });
  const finalizeOne = (item: any) => {
    const invoiceId = window
      .prompt("Invoice UUID to receive this usage line")
      ?.trim();
    if (invoiceId) finalize.mutate({ id: item.id, invoiceId });
  };
  return (
    <div className="space-y-4">
      <Panel>
        <h3 className="font-bold">Calculate a billing period</h3>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Input
            label="Tenant UUID"
            value={form.tenantId}
            onChange={(value) => setForm({ ...form, tenantId: value })}
          />
          <Select
            label="Meter"
            value={form.meterCode}
            values={["", ...meters.map((item) => item.code)]}
            onChange={(value) => setForm({ ...form, meterCode: value })}
          />
          <Input
            type="date"
            label="Period start"
            value={form.periodStart}
            onChange={(value) => setForm({ ...form, periodStart: value })}
          />
          <Input
            type="date"
            label="Period end (exclusive)"
            value={form.periodEnd}
            onChange={(value) => setForm({ ...form, periodEnd: value })}
          />
        </div>
        <div className="mt-4">
          <Button
            disabled={!form.tenantId || !form.meterCode}
            onClick={() => calculate.mutate()}
          >
            Calculate usage
          </Button>
        </div>
      </Panel>
      <div className="space-y-3">
        {items.length ? (
          items.map((item) => (
            <Panel key={item.id}>
              <div className="flex flex-wrap justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold">
                      {item.tenant?.name} · {item.meter?.code}
                    </h3>
                    <Badge value={item.status} />
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    {item.quantity} total · {item.billableQuantity} billable · ₹
                    {Number(item.chargeAmount).toFixed(2)}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    {new Date(item.periodStart).toLocaleDateString()} –{" "}
                    {new Date(item.periodEnd).toLocaleDateString()}
                  </p>
                </div>
                {item.status === "OPEN" && (
                  <Button onClick={() => finalizeOne(item)}>
                    Finalize to invoice
                  </Button>
                )}
              </div>
            </Panel>
          ))
        ) : (
          <Empty text="No usage aggregates." />
        )}
      </div>
    </div>
  );
}

function RevenuePanel({
  items,
  forecast,
  onChanged,
}: {
  items: any[];
  forecast: any[];
  onChanged: () => void;
}) {
  const [invoiceId, setInvoiceId] = useState("");
  const create = useMutation({
    mutationFn: () =>
      usageRevenueService.createRevenueSchedule(invoiceId, {
        reason:
          "Administrator created straight-line revenue recognition schedule",
      }),
    onSuccess: () => {
      onChanged();
      toast.success("Revenue schedule created");
    },
  });
  const recognize = useMutation({
    mutationFn: (id: string) =>
      usageRevenueService.recognizeRevenue(id, {
        reason: "Administrator recognized all due revenue periods",
      }),
    onSuccess: (result: any) => {
      onChanged();
      toast.success(`${result.recognized} periods recognized`);
    },
  });
  return (
    <div className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-[1fr_2fr]">
        <Panel>
          <h3 className="font-bold">Create from invoice</h3>
          <div className="mt-4">
            <Input
              label="Subscription invoice UUID"
              value={invoiceId}
              onChange={setInvoiceId}
            />
          </div>
          <div className="mt-4">
            <Button disabled={!invoiceId} onClick={() => create.mutate()}>
              Create schedule
            </Button>
          </div>
        </Panel>
        <Panel>
          <h3 className="font-bold">Pending revenue forecast</h3>
          <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {forecast.length ? (
              forecast.map((item) => (
                <div key={item.month} className="rounded-xl bg-slate-50 p-3">
                  <p className="text-[10px] font-bold uppercase text-slate-400">
                    {item.month}
                  </p>
                  <p className="mt-1 font-black">
                    ₹{Number(item.amount).toLocaleString()}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500">
                No deferred revenue forecast.
              </p>
            )}
          </div>
        </Panel>
      </div>
      <div className="space-y-3">
        {items.length ? (
          items.map((item) => (
            <Panel key={item.id}>
              <div className="flex flex-wrap justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold">
                      {item.invoice?.invoiceNumber} · {item.tenant?.name}
                    </h3>
                    <Badge value={item.status} />
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    ₹{Number(item.totalAmount).toFixed(2)} · {item.method}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    {new Date(item.startDate).toLocaleDateString()} –{" "}
                    {new Date(item.endDate).toLocaleDateString()} ·{" "}
                    {item.periods?.filter(
                      (period: any) => period.status === "RECOGNIZED",
                    ).length ?? 0}
                    /{item.periods?.length ?? 0} periods recognized
                  </p>
                </div>
                {item.status === "ACTIVE" && (
                  <Button onClick={() => recognize.mutate(item.id)}>
                    Recognize due periods
                  </Button>
                )}
              </div>
            </Panel>
          ))
        ) : (
          <Empty text="No revenue schedules." />
        )}
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: any;
}) {
  return (
    <Panel>
      <div className="flex justify-between">
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-black">{value}</p>
        </div>
        <Icon className="h-5 w-5 text-blue-600" />
      </div>
    </Panel>
  );
}
function TabBar<T extends readonly string[]>({
  values,
  value,
  onChange,
}: {
  values: T;
  value: T[number];
  onChange: (value: T[number]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2">
      {values.map((item) => (
        <button
          key={item}
          onClick={() => onChange(item)}
          className={`rounded-xl px-4 py-2 text-xs font-bold ${value === item ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`}
        >
          {item}
        </button>
      ))}
    </div>
  );
}
function Panel({ children }: { children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      {children}
    </section>
  );
}
function Empty({ text }: { text: string }) {
  return (
    <Panel>
      <p className="text-sm text-slate-500">{text}</p>
    </Panel>
  );
}
function Badge({ value }: { value: string }) {
  return (
    <span className="rounded-full bg-slate-100 px-2 py-1 text-[9px] font-bold uppercase text-slate-600">
      {value}
    </span>
  );
}
function Button({
  children,
  onClick,
  disabled,
  secondary = false,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  secondary?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold disabled:opacity-50 ${secondary ? "border border-slate-200 bg-white text-slate-700" : "bg-slate-900 text-white"}`}
    >
      {children}
    </button>
  );
}
function Input({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <label>
      <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
      />
    </label>
  );
}
function Select({
  label,
  value,
  values,
  labels,
  onChange,
}: {
  label: string;
  value: string;
  values: string[];
  labels?: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label>
      <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
      >
        {values.map((item, index) => (
          <option key={item} value={item}>
            {labels?.[index] ?? item}
          </option>
        ))}
      </select>
    </label>
  );
}
